<template>
  <Teleport to="body">
    <v-dialog
      :model-value="modelValue"
      max-width="520"
      persistent
      class="caller-dialog-root"
      @update:model-value="$emit('update:modelValue', $event)"
    >
      <SnackbarComp :SnackbarComponent="snackbar" />

      <div class="caller-dialog" role="dialog" aria-modal="true">
        <header class="caller-dialog__header">
          <div class="caller-dialog__brand">
            <div class="caller-dialog__icon" aria-hidden="true">
              <v-icon icon="mdi-file-upload-outline" size="22" color="white" />
            </div>
            <div>
              <h2 class="caller-dialog__title">Bulk upload team members</h2>
              <p class="caller-dialog__subtitle">
                Upload a spreadsheet with name, email, and contact_number columns.
              </p>
            </div>
          </div>
          <button
            type="button"
            class="caller-dialog__close"
            aria-label="Close"
            :disabled="uploading"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="caller-dialog__body">
          <button
            type="button"
            class="caller-dialog__template-link"
            @click="downloadTeamMembersTemplate"
          >
            <v-icon icon="mdi-download-outline" size="16" />
            Download Excel template
          </button>

          <v-file-input
            v-model="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            placeholder="Upload .xlsx / .xls / .csv"
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            :disabled="uploading"
            @update:model-value="onFileChange"
          />

          <p v-if="parseError" class="caller-dialog__meta caller-dialog__meta--error">
            {{ parseError }}
          </p>

          <p v-else-if="parsedMembers.length" class="caller-dialog__meta caller-dialog__meta--ok">
            {{ parsedMembers.length }} team member{{ parsedMembers.length === 1 ? '' : 's' }} ready to upload.
          </p>

          <div v-if="result" class="caller-dialog__result">
            <p class="caller-dialog__result-line">
              <v-icon icon="mdi-check-circle-outline" size="16" color="success" />
              {{ result.added_count }} added
            </p>
            <p v-if="result.skipped_count" class="caller-dialog__result-line">
              <v-icon icon="mdi-alert-circle-outline" size="16" color="warning" />
              {{ result.skipped_count }} skipped
            </p>
            <ul v-if="result.skipped?.length" class="caller-dialog__skipped-list">
              <li v-for="(row, index) in result.skipped" :key="index">
                {{ row.name || row.email || 'Row' }} ({{ row.email }}) — {{ row.reason }}
              </li>
            </ul>
          </div>
        </div>

        <footer class="caller-dialog__footer">
          <v-btn
            variant="text"
            class="caller-dialog__cancel"
            :disabled="uploading"
            @click="close"
          >
            {{ result ? 'Close' : 'Cancel' }}
          </v-btn>
          <v-btn
            v-if="!result"
            variant="flat"
            class="caller-dialog__submit"
            :loading="uploading"
            :disabled="!parsedMembers.length"
            @click="submit"
          >
            Upload {{ parsedMembers.length ? `(${parsedMembers.length})` : '' }}
          </v-btn>
        </footer>
      </div>
    </v-dialog>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue'
import SnackbarComp from '@/components/Extras/SnackbarComp.vue'
import { parseBulkTeamMembersFile, downloadTeamMembersTemplate } from '@/utils/teamMembersBulkUpload'
import { bulkCreateTeamMembers } from '@/api/teamMembersApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'

defineOptions({ name: 'BulkAddTeamMembersDialog' })

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'saved'])

const snackbar = ref({})
const file = ref(null)
const parsedMembers = ref([])
const parseError = ref('')
const uploading = ref(false)
const result = ref(null)

function reset() {
  file.value = null
  parsedMembers.value = []
  parseError.value = ''
  result.value = null
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) reset()
  },
)

async function onFileChange(newFile) {
  parseError.value = ''
  parsedMembers.value = []
  result.value = null
  if (!newFile) return

  const parsed = await parseBulkTeamMembersFile(newFile)
  if (!parsed.ok) {
    parseError.value = parsed.error
    return
  }
  parsedMembers.value = parsed.members
}

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  if (!parsedMembers.value.length) return

  uploading.value = true
  try {
    const response = await bulkCreateTeamMembers(parsedMembers.value)
    if (!isApiSuccess(response)) {
      snackbar.value = {
        SnackbarVmodel: true,
        SnackbarColor: 'error',
        SnackbarText: getApiMessage(response) || 'Failed to bulk-upload team members',
      }
      return
    }
    result.value = response.data || { added_count: 0, skipped_count: 0, skipped: [] }
    emit('saved')
  } catch (err) {
    snackbar.value = {
      SnackbarVmodel: true,
      SnackbarColor: 'error',
      SnackbarText: err?.message || 'Something went wrong',
    }
  } finally {
    uploading.value = false
  }
}
</script>

<style scoped>
.caller-dialog__result {
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}
.caller-dialog__result-line {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 4px;
  font-size: 0.875rem;
}
.caller-dialog__skipped-list {
  margin: 8px 0 0;
  padding-left: 18px;
  font-size: 0.8125rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  max-height: 160px;
  overflow-y: auto;
}
.caller-dialog__meta--error {
  color: #dc2626;
}
.caller-dialog__meta--ok {
  color: #16a34a;
}
.caller-dialog__template-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 16px;
  padding: 0;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e4dff;
  cursor: pointer;
}
.caller-dialog__template-link:hover {
  text-decoration: underline;
}
</style>
